// @file UI-only store for the Surface Live panel scaffold
import { unboundedWatch } from '@@/bits/vue'
import { trackLive, useSurfaceLiveActivityStore } from '@@/pinia/surface_live_activity_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceSidePanelsStore } from '@@/pinia/surface_side_panels'
import { defineStore } from 'pinia'
import { computed } from 'vue'

const useSurfaceLivePanelStore = defineStore('surfaceLivePanel', () => {
  const sidePanelsStore = useSurfaceSidePanelsStore()
  const liveActivityStore = useSurfaceLiveActivityStore()
  const permissionsStore = useSurfacePermissionsStore()
  // Open state lives on the side-panel registry so only one docked panel can be active.
  const xSurfaceLivePanel = computed(() => sidePanelsStore.isSidePanelOpen('live'))

  const canISeeLivePanel = computed(() =>
    liveActivityStore.activityStatus === 'running' ? liveActivityStore.isCreator : permissionsStore.canIAdminister,
  )

  // The panel is a teacher surface and opens before any activity exists, so the activity-derived
  // role in trackLive would read 'participant'. Administer rights are the role here.
  const panelRole = computed(() => (permissionsStore.canIAdminister ? 'creator' : 'participant'))

  function closeLivePanel(): void {
    if (!xSurfaceLivePanel.value) return
    sidePanelsStore.closeSidePanel('live')
    trackLive('Closed panel', { activityStatus: liveActivityStore.activityStatus, role: panelRole.value })
  }

  function openLivePanel(): void {
    if (xSurfaceLivePanel.value) return
    sidePanelsStore.openSidePanel('live')
    trackLive('Opened panel', { activityStatus: liveActivityStore.activityStatus, role: panelRole.value })
  }

  function toggleLivePanel(): void {
    // Route through the guarded open/close so the toggle emits exactly the transition it caused.
    if (xSurfaceLivePanel.value) closeLivePanel()
    else openLivePanel()
  }

  void unboundedWatch(canISeeLivePanel, (visible) => {
    if (!visible) closeLivePanel()
  })

  return { xSurfaceLivePanel, canISeeLivePanel, openLivePanel, closeLivePanel, toggleLivePanel }
})

export { useSurfaceLivePanelStore }
